import { Menu, MenuEntree, MenuItem, PortionItemType, MenuItemType } from '@/models';
import { freezeArray } from '.';
import { freezeObject, isDefined } from './util';
import head from 'lodash/head';

export function findEntreeInMenu(orderEntreeId: string, menu: Menu): MenuEntree | undefined {
  return (menu.entrees || []).find(e => e.itemId == orderEntreeId);
}

/** This does not search inside Entree.Contents for items */
export function findItemInMenu(itemId: string, menu: Menu): MenuItem | undefined {
  return getAllMenuItems(menu).find(i => i.itemId == itemId);
}

export function getAllMenuItems(menu: Menu, includeKidsSidesAndDrinks: boolean = false, includeUniqueEntreeItems: boolean = false): MenuItem[] {
  let allMenuItems = (<readonly MenuItem[]>(menu.entrees ?? [])).concat(head(menu.entrees)?.contents ?? []).concat(menu.sides ?? []).concat(menu.drinks ?? []).concat(menu.nonFoodItems ?? [] as any);

  if (includeKidsSidesAndDrinks) {
    allMenuItems = allMenuItems.concat(getKidsSidesAndDrinks(menu));
  }

  if (includeUniqueEntreeItems) {
    allMenuItems = allMenuItems.concat(getUniqueeEntreeItems(menu, allMenuItems));
  }

  return allMenuItems;
}

export function getKidsSidesAndDrinks(menu: Menu): MenuItem[] {
  // Just grabbing the first kids entree. All kids entrees share the same sides and drinks
  const kidsEntree = menu.entrees?.filter(e => e.itemType === 'KidsBYO' || e.itemType === 'KidsQuesadilla')[0];

  return kidsEntree?.contents?.filter(c => c.itemType === 'Beverage' || c.itemType === 'Side') ?? [];
}

// This should get items that exist in specific entrees (i.e, corn tortilla in tacos, honey vinaigrette in salads, etc.)
export function getUniqueeEntreeItems(menu: Menu, allMenuItems: MenuItem[]): MenuItem[] {
  const allEntreeContents = menu.entrees?.flatMap(e => e.contents);

  if (allEntreeContents) {
    const distinctEntreeContents = [...new Map(allEntreeContents.map(c => [c?.itemId, c])).values()].filter(isDefined);
    return distinctEntreeContents.filter(c => !allMenuItems.find(mi => mi.itemId == c?.itemId));
  }

  return [];
}

export function getPortionItemType(item: MenuItem): PortionItemType | undefined {
  return isPortionItemType(item.itemType) ? item.itemType : undefined;
}

export function isPortionItemType(type: MenuItemType): type is PortionItemType {
  const portionItemType = type as PortionItemType;

  return portionItemType == 'ExtraPortion' || portionItemType == 'HalfPortion';
}

export function freezeMenu<T extends Menu | undefined>(menu: T): T {
  if (!menu) {
    return menu;
  }

  freezeArray(menu.entrees || []);
  freezeArray(menu.sides || []);
  freezeArray(menu.drinks || []);
  freezeObject(menu as Menu);

  return menu;
}
